import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';

import cx from 'classnames';
import _ from 'lodash';
import Scrollbars from 'react-custom-scrollbars-2';

import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import Modal from 'components/Modal/Modal';

import AddDevice from './AddDevice';
import AddNote from './AddNote';
import BiometricDetails from './BiometricDetails';
import styles from './BiometricDevices.module.scss';
import BiometricTableRow from './BiometricTableRow';
import BiometricDveicesApi from '../../../../api/BiometricDevicesApi';
import Search from '../../../../components/Search';
import TableSkeleton from '../../../../components/skeletons/TableSkeleton';
import TableHead from '../../../../components/table/TableHead';
import ClientContext from '../ClientContext';

const headerCells = [
  { title: 'Name', sortable: true, sortValue: 'name' },
  { title: 'Serial Number', sortable: true, sortValue: 'serialNumber' },
  { title: 'Protocol' },
  { title: 'Last sync' },
  { title: 'Configured at', colSpan: 2 },
];

const BiometricDevices = ({ addDeviceModal, onClose, onEditOpen }) => {
  const { selected } = useContext(ClientContext);
  const param = selected?.id;
  const scope = useRef({ tenant: param });
  if (scope.current.tenant !== param) scope.current = { tenant: param };
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const deviceRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState({ option: 'name', direction: 'asc' });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [billingVerified, setBillingVerified] = useState(false);
  const [billingSubmitted, setBillingSubmitted] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [addNoteModal, setAddNoteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [retryAction, setRetryAction] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const noteOperation = useRef(null);
  const [notePending, setNotePending] = useState(false);
  const [UnbilledModal, setUnbilledModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [currentActionId, setCurrentActionId] = useState(null);

  const getAllDevices = useCallback(async () => {
    const { current } = scope;
    if (param == null || current.tenant !== param) return;
    const request = {};
    current.list = request;
    current.listPending = true;
    setLoading(true);
    setLoadError('');
    try {
      const res = await BiometricDveicesApi.getDevices(param);
      if (
        !Array.isArray(res.data?.items) ||
        !res.data.items.every((item) => item && [null, false, true].includes(item.isAddedToUnbilledCharges))
      )
        throw new Error('Invalid device list');
      if (scope.current === current && current.list === request) {
        setDevices(res.data.items);
        current.billingVerified = true;
        setBillingVerified(true);
        setBillingSubmitted(false);
      }
    } catch {
      if (scope.current === current && current.list === request) setLoadError('Unable to load devices. Please retry.');
    } finally {
      if (scope.current === current && current.list === request) {
        current.listPending = false;
        setLoading(false);
      }
    }
  }, [param]);

  useEffect(() => {
    noteOperation.current = null;
    setNotePending(false);
    setDevices([]);
    setBillingVerified(false);
    setBillingSubmitted(false);
    setSelectedDevice(null);
    deviceRef.current = null;
    setSelectedNote(null);
    setDetailsModal(false);
    setAddNoteModal(false);
    setUnbilledModal(false);
    setSaving(false);
    setLoadingAction(null);
    setCurrentActionId(null);
    setErrorMessage('');
    setRetryAction(null);
    setSearchValue('');
    getAllDevices();
    const { current } = scope;
    return () => {
      if (scope.current === current) scope.current = { tenant: undefined };
    };
  }, [getAllDevices]);

  const filteredDevices = useMemo(() => {
    const query = searchValue.toLowerCase();
    return _.orderBy(
      devices.filter((device) =>
        [device.name, device.serialNumber].some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(query)
        )
      ),
      [sort.option],
      [sort.direction]
    );
  }, [devices, searchValue, sort]);
  const onSort = (option, isAsc) => setSort({ option, direction: isAsc ? 'asc' : 'desc' });

  const runAction = async (action, id, request, success, deviceScoped = false, mutates = true) => {
    const { current } = scope;
    if (current.tenant !== param || current.action) return;
    const device = deviceRef.current;
    const token = {};
    current.action = token;
    const active = () =>
      scope.current === current && current.action === token && (!deviceScoped || deviceRef.current === device);
    setSaving(true);
    setLoadingAction(action);
    setCurrentActionId(id);
    setErrorMessage('');
    setRetryAction(null);
    try {
      const response = await request();
      if (active()) {
        if (mutates) current.list = null;
        success(response);
        // Every mutation replaces obsolete reads; read-only actions preserve them.
        if (mutates) getAllDevices();
      }
    } catch (error) {
      if (active()) {
        setErrorMessage(
          error.response?.status === 409 && action === 'save note'
            ? 'This note operation was refused. Refresh device notes before deliberately starting a new note.'
            : `Unable to ${action}. Please retry.`
        );
        setRetryAction(() => () => {
          // A saved callback must obey the current scope and eligibility too.
          if (
            scope.current !== current ||
            (deviceScoped && deviceRef.current !== device) ||
            (action === 'add unbilled charges' &&
              (!current.billingVerified || current.listPending || !current.billingCount))
          )
            return;
          runAction(action, id, request, success, deviceScoped, mutates);
        });
      }
    } finally {
      if (scope.current === current && current.action === token) {
        current.action = null;
        setSaving(false);
        setLoadingAction(null);
        setCurrentActionId(null);
      }
    }
  };

  const closeModalHandler = () => {
    if (saving) return;
    onClose(false);
    setSelectedDevice(null);
    deviceRef.current = null;
    setErrorMessage('');
    setRetryAction(null);
  };
  const selectDevice = (device) => {
    if (scope.current.action) return;
    deviceRef.current = device;
    setSelectedDevice(device);
    setErrorMessage('');
    setRetryAction(null);
    setDetailsModal(true);
  };
  const onEdit = (id) =>
    runAction(
      'load device',
      id,
      () => BiometricDveicesApi.getDeviceById(id),
      (res) => {
        deviceRef.current = res.data;
        setSelectedDevice(res.data);
        onEditOpen(true);
      },
      true,
      false
    );
  const onSave = (isEdit, data) =>
    runAction(
      'save device',
      selectedDevice?.id,
      () =>
        isEdit
          ? BiometricDveicesApi.updateDevice(selectedDevice.id, data)
          : BiometricDveicesApi.createDevice(param, data),
      () => {
        onClose(false);
        setSelectedDevice(null);
        deviceRef.current = null;
      },
      true
    );
  const onDelete = (id) =>
    runAction(
      'remove device',
      id,
      () => BiometricDveicesApi.deleteDevice(id),
      () => {
        setDevices((rows) => rows.filter((row) => row.id !== id));
        if (deviceRef.current?.id === id) {
          setDetailsModal(false);
          setSelectedDevice(null);
          deviceRef.current = null;
        }
      }
    );
  const onMakeBillable = (id) =>
    runAction(
      'update billing status',
      id,
      () => BiometricDveicesApi.updateDevice(id, { isAddedToUnbilledCharges: false }),
      () => {
        setDevices((rows) => rows.map((row) => (row.id === id ? { ...row, isAddedToUnbilledCharges: false } : row)));
      }
    );
  const onAddNote = (id) =>
    runAction(
      'load device for note',
      id,
      () => BiometricDveicesApi.getDeviceById(id),
      (res) => {
        deviceRef.current = res.data;
        setSelectedDevice(res.data);
        noteOperation.current = null;
        setNotePending(false);
        setSelectedNote(null);
        setAddNoteModal(true);
        setDetailsModal(false);
      },
      true,
      false
    );
  const onEditNote = (id) =>
    runAction(
      'load note',
      id,
      () => BiometricDveicesApi.getNote(selectedDevice.id, id),
      (res) => {
        setSelectedNote(res.data);
        setAddNoteModal(true);
        setDetailsModal(false);
      },
      true,
      false
    );
  const onSaveNote = (isEditNote, data) => {
    if (!isEditNote && !noteOperation.current) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      noteOperation.current = {
        scope: scope.current,
        device: selectedDevice.id,
        key: Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(''),
        payload: Object.freeze({ content: data.content }),
      };
      setNotePending(true);
    }
    const operation = noteOperation.current;
    if (!isEditNote && (operation.scope !== scope.current || operation.device !== selectedDevice.id)) return undefined;
    return runAction(
      'save note',
      selectedDevice?.id,
      () =>
        isEditNote
          ? BiometricDveicesApi.editNote(selectedDevice.id, selectedNote.id, data)
          : BiometricDveicesApi.createNote(operation.device, operation.payload, operation.key),
      () => {
        noteOperation.current = null;
        setNotePending(false);
        setAddNoteModal(false);
        setDetailsModal(true);
        setSelectedNote(null);
      },
      true
    );
  };
  const count = billingVerified ? devices.filter((item) => item.isAddedToUnbilledCharges === false).length : null;
  scope.current.billingCount = count;
  const onConfirm = () =>
    scope.current.billingVerified &&
    !scope.current.listPending &&
    count > 0 &&
    !loading &&
    runAction(
      'add unbilled charges',
      null,
      () => {
        if (!scope.current.billingVerified || scope.current.listPending || !scope.current.billingCount)
          throw new Error('Unverified billing eligibility');
        return BiometricDveicesApi.addToUnbilled(param);
      },
      () => {
        scope.current.billingVerified = false;
        setBillingVerified(false);
        setBillingSubmitted(true);
        setUnbilledModal(false);
      }
    );
  const closeNoteModalHandler = () => {
    if (saving) return;
    setAddNoteModal(false);
    setDetailsModal(true);
    setSelectedNote(null);
    setErrorMessage('');
    setRetryAction(null);
  };

  return (
    <div className='h-100' style={{ overflowY: 'hidden' }}>
      {loadError && (
        <div role='alert'>
          {loadError}{' '}
          <button type='button' onClick={getAllDevices} disabled={loading}>
            Retry
          </button>
        </div>
      )}
      {billingSubmitted && <p role='status'>Charges added. Refresh devices to verify billing eligibility.</p>}
      {loading && devices.length > 0 && <p role='status'>Refreshing devices…</p>}
      {errorMessage && (
        <div role='alert'>
          {errorMessage}{' '}
          {retryAction && (
            <button type='button' disabled={saving} onClick={retryAction}>
              Retry action
            </button>
          )}
        </div>
      )}
      <div className='d-flex justify-content-between my-3 me-5'>
        <div className='d-flex'>
          {!searchValue && filteredDevices.length === 0 ? (
            ''
          ) : (
            <Search
              onChange={(e) => {
                setSearchValue(e.target.value.replace(/^\s+/g, ''));
              }}
              placeholder='Search device'
              value={searchValue}
              onClear={() => {
                setSearchValue('');
              }}
            />
          )}
        </div>
        <div className='d-flex flex-end'>
          {count > 0 && (
            <button
              type='button'
              onClick={() => {
                setUnbilledModal(true);
              }}
              disabled={saving || loading}
              className={styles.btnDevices}
            >
              <div className='d-flex align-items-center justify-content-between'>
                <i className='icon-plus me-2' />
                <span> Add To Unbilled Charges</span>
              </div>
            </button>
          )}
        </div>
      </div>
      {filteredDevices.length === 0 && !loading && !loadError ? (
        <div className='pt-8 m-auto'>
          <div className={cx(styles.emptyList, 'm-auto')}>
            <div className={cx(styles.iconHistory, 'd-flex', 'justify-content-center', 'align-items-center', 'm-auto')}>
              <i className={cx(styles.icon, 'icon-pos')} />
            </div>
            <p className='mt-4 text-center text-capitalize'>No devices</p>
          </div>
        </div>
      ) : (
        <Scrollbars
          className='scroll-bar'
          autoHide
          renderView={({ style, ...props }) => <div {...props} style={{ ...style, overflowX: 'auto' }} />}
          style={{ height: 'calc(100vh - 275px)' }}
        >
          <div className='pe-5 pb-7 mb-7'>
            <table className='w-100 client-list' style={{ minWidth: 650 }}>
              <TableHead headerCells={headerCells} onSort={onSort} className={styles.fixedHeader} />
              <tbody>
                {filteredDevices.length > 0 &&
                  filteredDevices.map((item) => (
                    <BiometricTableRow
                      key={item.id}
                      data={item}
                      onEdit={onEdit}
                      onEditOpen={addDeviceModal}
                      onMakeBillable={onMakeBillable}
                      onDelete={onDelete}
                      onAddNote={onAddNote}
                      onSelect={() => {
                        setDetailsModal(true);
                        selectDevice(item);
                      }}
                      loadingAction={loadingAction}
                      currentActionId={currentActionId}
                    />
                  ))}
                {loading && devices.length === 0 && (
                  <tr className='w-100 client-list'>
                    <td colSpan='6'>
                      <TableSkeleton lineHight={45} mb='mb-2' num={5} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Scrollbars>
      )}
      <Modal
        isOpen={addDeviceModal}
        shouldCloseOnOverlayClick
        title={selectedDevice ? 'Edit Device' : 'Add Device'}
        styles={{
          width: 'min(560px, 94vw)',
          padding: 0,
        }}
        onRequestClose={() => {
          closeModalHandler(false);
        }}
      >
        <AddDevice
          onClose={closeModalHandler}
          tenant={selected?.id}
          selectedDevice={selectedDevice}
          onSave={onSave}
          saving={saving}
          saved={saved}
          errorMessageOnSave={errorMessage}
        />
      </Modal>
      <Modal
        isOpen={detailsModal}
        shouldCloseOnOverlayClick
        title={selectedDevice?.name}
        styles={{
          width: 'min(650px, 94vw)',
          padding: 0,
        }}
        onRequestClose={() => {
          setDetailsModal(false);
        }}
      >
        <BiometricDetails
          key={`${param}:${selectedDevice?.id}`}
          id={selectedDevice?.id}
          tenant={param}
          actionLoading={saving}
          onEditNote={onEditNote}
          onAddNote={onAddNote}
          onClose={() => {
            setDetailsModal(false);
            setSelectedDevice(null);
            deviceRef.current = null;
          }}
        />
      </Modal>
      <Modal
        isOpen={addNoteModal}
        shouldCloseOnOverlayClick
        title={selectedNote ? 'Edit note' : `Add Note for device: ${selectedDevice?.name}`}
        styles={{
          width: 'min(640px, 94vw)',
          padding: 0,
        }}
        onRequestClose={closeNoteModalHandler}
      >
        <AddNote
          onClose={closeNoteModalHandler}
          selectedDevice={selectedDevice}
          onSaveNote={onSaveNote}
          creationPending={notePending}
          selectedNote={selectedNote}
          saving={saving}
          saved={saved}
          errorMessageOnSave={errorMessage}
        />
      </Modal>
      <ConfirmationModal
        isOpen={UnbilledModal && billingVerified}
        responseStatus={errorMessage ? { resolved: true, isFailed: true, msg: errorMessage } : {}}
        isLoading={saving}
        onAction={onConfirm}
        onClose={() => {
          setUnbilledModal(false);
        }}
        description={`Number of unbilled devices: ${count}, Do 
        you want to add all unbilled devices to unbilled charges?`}
        title={'Add to unbilled charges'}
        type={'info'}
        actionText={'Confirm'}
      />
    </div>
  );
};

export default BiometricDevices;
